/**
 * Browser notification + bell sound for goal/VAR signals on enabled watchlist
 * matches. Purely a side effect on the signal stream `useLiveState()` already
 * holds — no new data source. Notification permission and the shared
 * AudioContext can only be unlocked from a real user gesture (browser
 * autoplay/permission policy), so `requestPermission()` must be called from a
 * click handler, not on mount.
 */
import { useEffect, useRef, useState } from "react";
import type { MatchSignal, WatchGroup } from "../types.ts";

/** The signal types worth interrupting the operator for. Exported so the
 *  auto-open hook (web/auto-open.ts) alarms on exactly the same events as the
 *  bell does — one definition, no drift. */
export const ALERT_TYPES = new Set(["GOAL", "OWN_GOAL", "PENALTY_GOAL", "PENALTY", "GOAL_CANCELED", "VAR", "VAR_OVER"]);

const STORAGE_KEY = "signalAlertsEnabled";

let sharedCtx: AudioContext | null = null;
function audioCtx(): AudioContext {
  if (!sharedCtx) sharedCtx = new AudioContext();
  return sharedCtx;
}

/** Two-tone bell: a fundamental + a fifth overtone, both with a fast exponential decay. */
function playBell() {
  const ctx = audioCtx();
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.3, now);
  master.connect(ctx.destination);

  for (const [freq, delay, gain] of [
    [880, 0, 0.5],
    [1320, 0.02, 0.3],
  ] as const) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    g.gain.setValueAtTime(gain, now + delay);
    g.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.9);
    osc.connect(g);
    g.connect(master);
    osc.start(now + delay);
    osc.stop(now + delay + 1);
  }
}

function describeSignal(s: MatchSignal, group: WatchGroup | undefined): { title: string; body: string } {
  const teams = group?.label ?? (s.homeTeam && s.awayTeam ? `${s.homeTeam} – ${s.awayTeam}` : s.match);
  const kind = s.type.startsWith("VAR") ? "VAR" : "Goal";
  const parts = [s.team ?? "", s.minute != null ? `${s.minute}'` : "", s.score ?? "", s.note ?? ""].filter(Boolean);
  return { title: `${kind}: ${teams}`, body: `${s.type}${parts.length ? " — " + parts.join(" · ") : ""}` };
}

export function useSignalAlerts(signals: MatchSignal[], watchGroups: WatchGroup[]) {
  const [enabled, setEnabledState] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const [permission, setPermission] = useState<NotificationPermission>(() => (typeof Notification !== "undefined" ? Notification.permission : "denied"));
  // Only signals arriving after this hook is mounted may alert. Marking what's
  // already delivered as seen covers a WS reconnect (it re-sends known ids), but
  // NOT a page load: `signals` is empty at mount and the hello backlog lands in
  // the first effect run — so that first batch seeds `seen` and rings nothing.
  const seen = useRef<Set<string>>(new Set(signals.map((s) => s.id)));
  const primed = useRef(false);
  const groupsRef = useRef(watchGroups);
  groupsRef.current = watchGroups;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const fresh = signals.filter((s) => !seen.current.has(s.id));
    if (fresh.length === 0) return;
    for (const s of fresh) seen.current.add(s.id);
    if (!primed.current) {
      primed.current = true; // the hello backlog: seed only
      return;
    }
    if (!enabledRef.current) return;

    let rang = false;
    for (const s of fresh) {
      if (!ALERT_TYPES.has(s.type)) continue;
      const group = groupsRef.current.find((g) => g.id === s.match);
      if (!group?.enabled) continue;
      if (!rang) {
        playBell();
        rang = true;
      }
      if (permission === "granted") {
        const { title, body } = describeSignal(s, group);
        new Notification(title, { body });
      }
    }
  }, [signals, permission]);

  const setEnabled = (v: boolean) => {
    localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
    setEnabledState(v);
  };

  const requestPermission = () => {
    audioCtx().resume();
    if (typeof Notification === "undefined") return;
    void Notification.requestPermission().then(setPermission);
  };

  return { enabled, setEnabled, permission, requestPermission };
}
